import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Target, Zap, RotateCcw, Circle, Droplet, Diamond, Scale, Euro, Trophy, Sparkles, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';

const QUESTIONS = [
  {
    id: 'level',
    icon: Trophy,
    options: [
      { value: 'beginner', icon: '🌱' },
      { value: 'intermediate', icon: '📈' },
      { value: 'advanced', icon: '🎯' },
      { value: 'expert', icon: '🏆' },
    ]
  },
  {
    id: 'play_type',
    icon: Target,
    options: [
      { value: 'control', icon: '🎯', iconComponent: Target },
      { value: 'power', icon: '💥', iconComponent: Zap },
      { value: 'polyvalent', icon: '⚖️', iconComponent: RotateCcw },
    ]
  },
  {
    id: 'shape',
    icon: Circle,
    options: [
      { value: 'ronde', icon: '⭕', iconComponent: Circle },
      { value: 'goutte', icon: '💧', iconComponent: Droplet },
      { value: 'diamant', icon: '💎', iconComponent: Diamond },
      { value: 'no_pref', icon: '🤷' },
    ]
  },
  {
    id: 'weight',
    icon: Scale,
    options: [
      { value: 'light', icon: '🪶' },
      { value: 'medium', icon: '⚖️' },
      { value: 'heavy', icon: '🏋️' },
      { value: 'no_pref', icon: '🤷' },
    ]
  },
  {
    id: 'budget',
    icon: Euro,
    options: [
      { value: 'low', icon: '💰' },
      { value: 'medium', icon: '💵' },
      { value: 'high', icon: '💎' },
      { value: 'premium', icon: '👑' },
    ]
  },
];

const GuidePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const currentQuestion = QUESTIONS[currentStep];
  const progress = ((currentStep) / QUESTIONS.length) * 100;

  const handleSelect = (value) => {
    const newAnswers = { ...answers, [currentQuestion.id]: value };
    setAnswers(newAnswers);
    
    // Auto-advance after selection with a small delay
    setTimeout(() => {
      if (currentStep < QUESTIONS.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        setShowResults(true);
      }
    }, 300);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const buildSearchParams = () => {
    const params = new URLSearchParams();
    
    // Map answers to filter params
    if (answers.play_type && answers.play_type !== 'no_pref') {
      params.set('play_type', answers.play_type);
    }
    if (answers.shape && answers.shape !== 'no_pref') {
      params.set('shape', answers.shape);
    }
    if (answers.weight && answers.weight !== 'no_pref') {
      params.set('weight', answers.weight);
    }
    
    // Budget mapping
    if (answers.budget) {
      switch (answers.budget) {
        case 'low': params.set('max_price', '50'); break;
        case 'medium': params.set('min_price', '50'); params.set('max_price', '100'); break;
        case 'high': params.set('min_price', '100'); params.set('max_price', '150'); break;
        case 'premium': params.set('min_price', '150'); break;
        default: break;
      }
    }
    
    return params.toString();
  };

  const getRecommendationText = () => {
    const level = answers.level;
    const playType = answers.play_type;
    
    let text = t('guide.reco_intro');
    
    if (level === 'beginner') {
      text += t('guide.reco_beginner');
    } else if (level === 'expert') {
      text += t('guide.reco_expert');
    }
    
    if (playType === 'control') {
      text += t('guide.reco_control');
    } else if (playType === 'power') {
      text += t('guide.reco_power');
    } else {
      text += t('guide.reco_poly');
    }
    
    return text;
  };

  const getLevelRecommendations = () => {
    const lvl = ['beginner', 'intermediate', 'advanced', 'expert'].includes(answers.level) ? answers.level : 'intermediate';
    return [1, 2, 3].map(i => t(`guide.rec_${lvl}_${i}`));
  };

  if (showResults) {
    const searchParams = buildSearchParams();
    const recommendations = getLevelRecommendations();
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F7FAFB] to-white">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-[#32CD32] to-[#28a428] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#32CD32]/30">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-black text-[#033871] mb-2">{t('guide.profile_ready')}</h1>
            <p className="text-[#5A8BA8]">{t('guide.personalized')}</p>
          </div>

          {/* Profile Summary */}
          <div className="bg-white rounded-2xl border border-[#E8F4FD] p-6 mb-6 shadow-sm">
            <h2 className="font-bold text-[#033871] mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-[#0C63BF]" /> {t('guide.your_profile')}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(answers).map(([key, value]) => {
                const question = QUESTIONS.find(q => q.id === key);
                const option = question?.options.find(o => o.value === value);
                return (
                  <div key={key} className="bg-[#F7FAFB] rounded-xl p-3 text-center">
                    <p className="text-2xl mb-1">{option?.icon}</p>
                    <p className="text-xs text-[#5A8BA8]">{t(`guide.label_${key}`)}</p>
                    <p className="font-semibold text-[#033871] text-sm">{option ? t(`guide.opt_${key}_${option.value}`) : ''}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-gradient-to-br from-[#033871] to-[#0C63BF] rounded-2xl p-6 mb-6 text-white">
            <h2 className="font-bold mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#32CD32]" /> {t('guide.our_reco')}
            </h2>
            <p className="text-white/90 text-sm leading-relaxed mb-4">{getRecommendationText()}</p>
            <div className="grid grid-cols-3 gap-3">
              {recommendations.map((rec, i) => (
                <div key={i} className="bg-white/10 rounded-lg p-3 text-center">
                  <Check className="w-4 h-4 text-[#32CD32] mx-auto mb-1" />
                  <p className="text-xs">{rec}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="space-y-3">
            <Button
              onClick={() => navigate(`/catalogue?${searchParams}`)}
              className="w-full h-14 bg-[#32CD32] hover:bg-[#28a428] text-white font-bold text-lg rounded-xl gap-2 shadow-lg shadow-[#32CD32]/30"
              data-testid="see-results-btn"
            >
              {t('guide.see_results')} <ChevronRight className="w-5 h-5" />
            </Button>
            <Button
              onClick={() => { setShowResults(false); setCurrentStep(0); setAnswers({}); }}
              variant="outline"
              className="w-full h-12 border-[#D6E8F0] text-[#5A8BA8] rounded-xl"
            >
              {t('guide.restart_quiz')}
            </Button>
          </div>

          {/* Trust badges */}
          <div className="mt-8 pt-6 border-t border-[#E8F4FD]">
            <div className="flex items-center justify-center gap-6 text-xs text-[#5A8BA8]">
              <span className="flex items-center gap-1"><Check className="w-4 h-4 text-[#32CD32]" /> {t('guide.badge_argus')}</span>
              <span className="flex items-center gap-1"><Check className="w-4 h-4 text-[#32CD32]" /> {t('guide.badge_score')}</span>
              <span className="flex items-center gap-1"><Check className="w-4 h-4 text-[#32CD32]" /> {t('guide.badge_verified')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F7FAFB] to-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-[#5A8BA8]">{t('guide.question_label', { n: currentStep + 1, total: QUESTIONS.length })}</span>
            <span className="text-xs font-medium text-[#0C63BF]">{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-[#E8F4FD] rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#0C63BF] to-[#32CD32] rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-2xl border border-[#E8F4FD] shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#033871] to-[#0C63BF] p-6 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <currentQuestion.icon className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-bold mb-1">{t(`guide.q_${currentQuestion.id}_title`)}</h2>
            <p className="text-white/70 text-sm">{t(`guide.q_${currentQuestion.id}_sub`)}</p>
          </div>

          {/* Options */}
          <div className="p-4 sm:p-6 space-y-3">
            {currentQuestion.options.map((option) => {
              const isSelected = answers[currentQuestion.id] === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`w-full p-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-4 text-left
                    ${isSelected 
                      ? 'border-[#32CD32] bg-[#32CD32]/5 shadow-md' 
                      : 'border-[#E8F4FD] hover:border-[#0C63BF] hover:bg-[#F7FAFB]'
                    }`}
                  data-testid={`option-${option.value}`}
                >
                  <span className="text-3xl">{option.icon}</span>
                  <div className="flex-1">
                    <p className={`font-bold ${isSelected ? 'text-[#32CD32]' : 'text-[#033871]'}`}>
                      {t(`guide.opt_${currentQuestion.id}_${option.value}`)}
                    </p>
                    <p className="text-sm text-[#5A8BA8]">{t(`guide.opt_${currentQuestion.id}_${option.value}_desc`)}</p>
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 bg-[#32CD32] rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="px-6 pb-6 flex items-center justify-between">
            <Button
              onClick={handleBack}
              variant="ghost"
              disabled={currentStep === 0}
              className="text-[#5A8BA8] gap-1"
            >
              <ChevronLeft className="w-4 h-4" /> {t('guide.back')}
            </Button>
            <div className="flex gap-1">
              {QUESTIONS.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentStep ? 'bg-[#0C63BF] w-4' : 
                    i < currentStep ? 'bg-[#32CD32]' : 'bg-[#E8F4FD]'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Skip link */}
        <p className="text-center mt-6">
          <button
            onClick={() => navigate('/catalogue')}
            className="text-sm text-[#5A8BA8] hover:text-[#0C63BF] underline"
          >
            {t('guide.skip')}
          </button>
        </p>
      </div>
    </div>
  );
};

export default GuidePage;
